import { useState } from 'react'
import { Button } from '#app/components/ui/button'
import { Icon } from '#app/components/ui/icon'
import { InputText } from '#app/components/ui/input-text'

interface AseguradoData {
	aseguradoNombre: string
	aseguradoCuit: string
	aseguradoDomicilio: string
	aseguradoTelefono: string
	aseguradoEmail: string
	sumaAsegurada: string
	vigencia: string
}

interface CaucionIGJAseguradoScreenProps {
	onNext: (data: AseguradoData) => void
	onBack: () => void
	data?: Partial<AseguradoData>
}

const VIGENCIA_OPTIONS = [
	{ value: '12-meses', label: '12 meses' },
	{ value: 'vigencia-mandato', label: 'Vigencia de mandato' },
]

export function CaucionIGJAseguradoScreen({
	onNext,
	onBack,
	data,
}: CaucionIGJAseguradoScreenProps) {
	const [formData, setFormData] = useState<AseguradoData>({
		aseguradoNombre: data?.aseguradoNombre || '',
		aseguradoCuit: data?.aseguradoCuit || '',
		aseguradoDomicilio: data?.aseguradoDomicilio || '',
		aseguradoTelefono: data?.aseguradoTelefono || '',
		aseguradoEmail: data?.aseguradoEmail || '',
		sumaAsegurada: data?.sumaAsegurada || '',
		vigencia: data?.vigencia || '12-meses',
	})

	const [errors, setErrors] = useState<Record<string, string>>({})

	const validateCUIT = (cuit: string) => {
		const cleaned = cuit.replace(/-/g, '')
		return cleaned.length === 11 && /^\d+$/.test(cleaned)
	}

	const formatCUIT = (value: string) => {
		const cleaned = value.replace(/\D/g, '')
		if (cleaned.length <= 2) {
			return cleaned
		} else if (cleaned.length <= 10) {
			return `${cleaned.slice(0, 2)}-${cleaned.slice(2)}`
		} else {
			return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 10)}-${cleaned.slice(10, 11)}`
		}
	}

	const formatCurrency = (value: string) => {
		const cleaned = value.replace(/\D/g, '')
		if (!cleaned) return ''
		return Number(cleaned).toLocaleString('es-AR')
	}

	const validateSumaAsegurada = (suma: string) => {
		const cleaned = suma.replace(/\D/g, '')
		const value = Number(cleaned)
		return value >= 300000 && value <= 1000000
	}

	const handleSubmit = () => {
		const newErrors: Record<string, string> = {}

		if (!formData.aseguradoNombre.trim()) {
			newErrors.aseguradoNombre = 'Ingresa la razón social'
		}
		if (!formData.aseguradoCuit.trim()) {
			newErrors.aseguradoCuit = 'Ingresa el CUIT'
		} else if (!validateCUIT(formData.aseguradoCuit)) {
			newErrors.aseguradoCuit = 'CUIT inválido (formato: XX-XXXXXXXX-X)'
		}
		if (!formData.aseguradoDomicilio.trim()) {
			newErrors.aseguradoDomicilio = 'Ingresa el domicilio'
		}
		if (!formData.sumaAsegurada.trim()) {
			newErrors.sumaAsegurada = 'Ingresa la suma asegurada'
		} else if (!validateSumaAsegurada(formData.sumaAsegurada)) {
			newErrors.sumaAsegurada = 'La suma debe estar entre $300.000 y $1.000.000'
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors)
			return
		}

		onNext(formData)
	}

	const isFormValid =
		formData.aseguradoNombre.trim() &&
		formData.aseguradoCuit.trim() &&
		formData.aseguradoDomicilio.trim() &&
		formData.sumaAsegurada.trim()

	return (
		<div className="flex h-full w-full flex-col">
			<div className="flex-1 overflow-y-auto">
				<div className="flex w-full flex-col items-center gap-6">
					<h2 className="font-parkinsans text-center text-2xl leading-8 font-medium text-blue-800">
						Datos de la Sociedad
						<br />
						<span className="text-lg text-neutral-600">(Asegurado)</span>
					</h2>

					<div className="flex w-full flex-col gap-4">
						<InputText
							label="Razón Social"
							labelPosition="internal"
							size="l"
							placeholder="Nombre de la sociedad"
							value={formData.aseguradoNombre}
							onChange={(e) => {
								setFormData({ ...formData, aseguradoNombre: e.target.value })
								setErrors({ ...errors, aseguradoNombre: '' })
							}}
							errorText={errors.aseguradoNombre}
							required
						/>
						<InputText
							label="CUIT de la Sociedad"
							labelPosition="internal"
							size="l"
							placeholder="00-00000000-0"
							value={formData.aseguradoCuit}
							onChange={(e) => {
								const formatted = formatCUIT(e.target.value)
								setFormData({ ...formData, aseguradoCuit: formatted })
								setErrors({ ...errors, aseguradoCuit: '' })
							}}
							errorText={errors.aseguradoCuit}
							maxLength={13}
							required
						/>
						<InputText
							label="Domicilio Legal"
							labelPosition="internal"
							size="l"
							placeholder="Dirección de la sociedad"
							value={formData.aseguradoDomicilio}
							onChange={(e) => {
								setFormData({ ...formData, aseguradoDomicilio: e.target.value })
								setErrors({ ...errors, aseguradoDomicilio: '' })
							}}
							errorText={errors.aseguradoDomicilio}
							required
						/>
						<InputText
							label="Teléfono (opcional)"
							labelPosition="internal"
							size="l"
							placeholder="Teléfono de la sociedad"
							value={formData.aseguradoTelefono}
							onChange={(e) => {
								setFormData({ ...formData, aseguradoTelefono: e.target.value })
							}}
						/>
						<InputText
							label="Email (opcional)"
							labelPosition="internal"
							size="l"
							placeholder="correo@empresa.com"
							type="email"
							value={formData.aseguradoEmail}
							onChange={(e) => {
								setFormData({ ...formData, aseguradoEmail: e.target.value })
							}}
						/>

						<div className="mt-4 border-t border-neutral-200 pt-4">
							<h3 className="font-parkinsans mb-4 text-lg font-medium text-blue-800">
								Cobertura
							</h3>

							<div className="mb-4">
								<InputText
									label="Suma Asegurada"
									labelPosition="internal"
									size="l"
									placeholder="Entre $300.000 y $1.000.000"
									value={formData.sumaAsegurada}
									onChange={(e) => {
										const formatted = formatCurrency(e.target.value)
										setFormData({ ...formData, sumaAsegurada: formatted })
										setErrors({ ...errors, sumaAsegurada: '' })
									}}
									errorText={errors.sumaAsegurada}
									required
								/>
								<p className="mt-1 text-xs text-neutral-500">
									Según Res. IGJ 15/2021: mínimo $300.000, máximo $1.000.000 por
									director
								</p>
							</div>

							<div>
								<label className="font-parkinsans mb-2 block text-sm font-medium text-neutral-700">
									Vigencia
								</label>
								<div className="flex flex-col gap-2">
									{VIGENCIA_OPTIONS.map((option) => (
										<label
											key={option.value}
											className="flex cursor-pointer items-center gap-3 rounded-lg border border-neutral-200 p-3 transition-colors hover:bg-neutral-50"
										>
											<input
												type="radio"
												name="vigencia"
												value={option.value}
												checked={formData.vigencia === option.value}
												onChange={(e) =>
													setFormData({ ...formData, vigencia: e.target.value })
												}
												className="size-4 text-green-600"
											/>
											<span className="font-parkinsans text-sm text-neutral-900">
												{option.label}
											</span>
										</label>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="flex w-full shrink-0 flex-col items-center justify-start gap-2 bg-white pt-4 md:flex-row md:justify-between md:bg-transparent md:pt-8">
				<Button
					variant="primary"
					appearance="ghost"
					size="l"
					onClick={onBack}
					className="order-2 w-full gap-2 rounded-xl px-4 py-3 text-sm leading-4 md:order-1 md:w-auto md:px-6 md:py-4 md:text-base md:leading-5"
				>
					<Icon name="arrow-left" className="size-4" />
					Atrás
				</Button>
				<Button
					variant="secondary"
					appearance="solid"
					size="l"
					onClick={handleSubmit}
					disabled={!isFormValid}
					className="order-1 w-full md:order-2 md:w-auto"
				>
					Enviar solicitud
					<Icon name="arrow-right" className="size-3.5" />
				</Button>
			</div>
		</div>
	)
}
